import classNames from 'classnames';
import NextIcon from '@udemy/icons/dist/next.ud-icon';
import PreviousIcon from '@udemy/icons/dist/previous.ud-icon';
import {observer, MobXProviderContext} from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import {Avatar} from '@udemy/react-core-components';
import {BlockList} from '@udemy/react-core-components';
import {SideDrawer, SubDrawer} from '@udemy/react-dialog-components';
import styles from './mobile-nav.module.less';

const MobileNav = React.forwardRef(({className, ...props}, ref) => (
    <SideDrawer
        ref={ref}
        id="header-toggle-side-nav"
        mainDrawerId="header-toggle-side-nav-main"
        title={gettext('Site navigation')}
        side="left"
        {...props}
        className={classNames(className, styles['nav'])}
    />
));

const MobileNavSectionHeading = ({className, ...props}) => {
    return (
        <div className={classNames(className, 'ud-heading-sm', styles['nav-section-heading'])} {...props} />
    );
};

MobileNav.SectionHeading = MobileNavSectionHeading;

const MobileNavSection = (props) => {
    return (
        <BlockList
            size="large"
            iconAlignment="right"
            className={styles['nav-section']}
            {...props}
        />
    );
};

MobileNav.Section = MobileNavSection;

const MobileNavItem = ({className, ...props}) => {
    const defaultProps = {color: 'neutral'};
    if ('cssToggleId' in props) {
        defaultProps.componentClass = 'button';
        defaultProps.icon = <NextIcon label={gettext('Expand')} />;
    }
    return <BlockList.Item {...defaultProps} {...props} className={classNames(className, styles['nav-item'])} />;
};

MobileNav.Item = MobileNavItem;

const MobileNavBackItem = ({className, ...props}) => (
    <BlockList.Item
        componentClass="button"
        cssToggleId="header-toggle-side-nav-main"
        color="neutral"
        icon={<PreviousIcon label={gettext('Go back')} />}
        {...props}
        className={classNames(className, styles['nav-item'], styles['highlighted'])}
    />
);

const MobileNavL1Nav = ({className, children, ...props}) => {
    const {mobileNavStore} = React.useContext(MobXProviderContext);
    const onSelect = () => mobileNavStore.ensureLevelIsLoaded(2);
    return (
        <SubDrawer level={1} onSelect={onSelect} {...props} className={classNames(className, styles['nav'])}>
            <BlockList size="large" padding="loose">
                <MobileNavBackItem>{gettext('Menu')}</MobileNavBackItem>
            </BlockList>
            {children}
        </SubDrawer>
    );
};

MobileNav.L1Nav = MobileNavL1Nav;

const MobileNavL2Nav = ({className, l1NavId, l1NavTitle, children, ...props}) => {
    return (
        <SubDrawer level={2} {...props} className={classNames(className, styles['nav'])}>
            <BlockList size="large" padding="loose">
                <MobileNavBackItem>{gettext('Menu')}</MobileNavBackItem>
                <MobileNavBackItem
                    cssToggleId={l1NavId}
                    className={classNames(styles['nav-item'], styles['underlined'])}
                >
                    {l1NavTitle}
                </MobileNavBackItem>
            </BlockList>
            {children}
        </SubDrawer>
    );
};

MobileNavL2Nav.propTypes = {
    l1NavId: PropTypes.string.isRequired,
    l1NavTitle: PropTypes.node.isRequired,
};

MobileNav.L2Nav = MobileNavL2Nav;

const MobileNavWelcomeSection = observer(({className, user, badge, ...props}) => {
    if (!user || !user.id) {
        return null;
    }
    return (
        <MobileNav.Section padding="loose" className={classNames(className, styles['welcome-section'])}>
            <MobileNav.Item
                {...props}
                className={classNames(styles['nav-item'], styles['highlighted'])}
            >
                <span className={styles['welcome-section-content']}>
                    <span style={{display: 'inline-flex', position: 'relative'}}>
                        <Avatar user={user} alt="NONE" />
                        {badge}
                    </span>
                    <span style={{flex: 1}}>
                        <span className={classNames('ud-heading-md', styles['profile-name'])}>
                            {interpolate(gettext('Hi, %(name)s'), {name: user.display_name}, true)}
                        </span>
                        <span className={classNames('ud-text-sm', styles['profile-welcome'])}>
                            {gettext('Welcome back')}
                        </span>
                    </span>
                </span>
            </MobileNav.Item>
        </MobileNav.Section>
    );
});

MobileNavWelcomeSection.propTypes = {
    user: PropTypes.object,
    badge: PropTypes.node,
};

MobileNavWelcomeSection.defaultProps = {
    user: null,
    badge: null,
};

MobileNav.WelcomeSection = MobileNavWelcomeSection;

export default MobileNav;
